/**
 * GuidePage — config-driven group guide (replaces vanilla renderGuideShell).
 * The active guide is resolved from the route id (e.g. /org-guide) against
 * GUIDE_CONFIGS. Presentational only — no data fetch.
 */
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Page } from '../components/Page';
import { Icon } from '../components/Icon';
import { GUIDE_CONFIGS } from './guides.config';

export function GuidePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const id = location.pathname.replace(/^\/+/, '').split('/')[0] ?? '';
  const cfg = GUIDE_CONFIGS[id];

  if (!cfg) {
    return (
      <Page title="คู่มือ">
        <div className="card p-6 text-sm text-gray-500">ไม่พบคู่มือสำหรับหน้านี้</div>
      </Page>
    );
  }

  return (
    <Page title={cfg.title} subtitle={cfg.subtitle}>
      {/* Hero */}
      <div className="rounded-2xl bg-brand-gradient p-5 text-white flex items-start gap-3">
        <div className="h-11 w-11 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
          <Icon name={cfg.icon} className="w-6 h-6" />
        </div>
        <p className="text-sm leading-relaxed opacity-95">{cfg.intro}</p>
      </div>

      {/* Steps */}
      {cfg.steps.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-700">ขั้นตอน</p>
          {cfg.steps.map((s, i) => (
            <div key={i} className="card p-4 flex gap-3">
              <div className="h-7 w-7 rounded-full bg-brand-50 text-brand-700 flex items-center justify-center text-sm font-semibold shrink-0 tabular-nums">
                {i + 1}
              </div>
              <div>
                <p className="text-sm font-medium">{s.title}</p>
                <p className="text-sm text-gray-500 mt-0.5">{s.body}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Examples */}
      {cfg.examples && cfg.examples.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {cfg.examples.map((ex, i) => (
            <div key={i} className="card p-4">
              <p className="text-sm font-medium">{ex.title}</p>
              <p className="text-sm text-gray-500 mt-1">{ex.scenario}</p>
            </div>
          ))}
        </div>
      )}

      {/* Related */}
      {cfg.related && cfg.related.length > 0 && (
        <div>
          <p className="text-sm font-medium text-gray-700 mb-2">เมนูที่เกี่ยวข้อง</p>
          <div className="flex flex-wrap gap-2">
            {cfg.related.map((r) => (
              <button
                key={r.route}
                onClick={() => navigate(`/${r.route}`)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
              >
                <Icon name={r.icon} className="w-4 h-4 text-brand-600" />
                {r.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* CTA buttons */}
      {cfg.ctaButtons && cfg.ctaButtons.length > 0 && (
        <div className="flex flex-wrap gap-2 pt-1">
          {cfg.ctaButtons.map((c) => (
            <button
              key={c.route}
              onClick={() => navigate(`/${c.route}`)}
              className={c.primary ? 'btn-cta' : 'btn-secondary'}
            >
              <Icon name={c.icon} className="w-4 h-4" />
              {c.label}
              {c.primary && <ArrowRight className="w-4 h-4" />}
            </button>
          ))}
        </div>
      )}
    </Page>
  );
}
